export const buildStrengthWeaknessSection = () => ({
  id: "strengthWeakness",
  title: "Strength and Weakness",
  stats: [
    { label: "Finishing", value: "-" },
    { label: "Pace", value: "-" },
    { label: "Dribbling", value: "-" },
    { label: "Passing", value: "-" },
    { label: "Defending", value: "-" },
    { label: "Physical", value: "-" },
    { label: "Overall Rating", value: "-" },
  ],
});

export const createFallbackStats = () => ({
  keyStats: [
    { key: "appearance", label: "Appearances", value: "-" },
    { key: "goals", label: "Goals", value: "-" },
    { key: "wins", label: "Wins", value: "-" },
    { key: "losses", label: "Losses", value: "-" },
    { key: "minutesPlayed", label: "Minutes Played", value: "-" },
  ],
  sections: [
    {
      id: "attacking",
      title: "Attacking",
      stats: [
        { label: "Goals", value: "-" },
        { label: "Assists", value: "-" },
        { label: "Shots Total", value: "-" },
        { label: "Shots On Target", value: "-" },
        { label: "Shots Off Target", value: "-" },
        { label: "Big Chances Missed", value: "-" },
        { label: "Big Chances Created", value: "-" },
      ],
    },
    {
      id: "defending",
      title: "Defending",
      stats: [
        { label: "Tackles", value: "-" },
        { label: "Interceptions", value: "-" },
        { label: "Clearances", value: "-" },
        { label: "Fouls", value: "-" },
        { label: "Yellow Cards", value: "-" },
        { label: "Goals Conceded", value: "-" },
        { label: "Clean Sheets", value: "-" },
      ],
    },
    {
      id: "passing",
      title: "Passing",
      stats: [
        { label: "Passes", value: "-" },
        { label: "Accurate Passes", value: "-" },
        { label: "Pass Accuracy", value: "-" },
        { label: "Key Passes", value: "-" },
        { label: "Long Balls", value: "-" },
        { label: "Crosses", value: "-" },
        { label: "Assists", value: "-" },
      ],
    },
    {
      id: "dribbling",
      title: "Dribbling",
      stats: [
        { label: "Dribble Attempts", value: "-" },
        { label: "Successful Dribbles", value: "-" },
        { label: "Dribble Success Rate", value: "-" },
        { label: "Dispossessed", value: "-" },
        { label: "Fouls Drawn", value: "-" },
        { label: "Total Duels", value: "-" },
        { label: "Duels Won", value: "-" },
      ],
    },
    buildStrengthWeaknessSection(),
    {
      id: "other",
      title: "Other",
      stats: [
        { label: "Minutes Played", value: "-" },
        { label: "Appearances", value: "-" },
        { label: "Matches Started", value: "-" },
        { label: "Substitutions", value: "-" },
        { label: "Touches", value: "-" },
        { label: "Average Rating", value: "-" },
        { label: "Offsides", value: "-" },
      ],
    },
  ],
  meta: { hasData: false, source: "fallback" },
});

const isNil = (value) =>
  value === null ||
  value === undefined ||
  (typeof value === "number" && Number.isNaN(value));

export const formatInteger = (value) => {
  if (isNil(value)) return "-";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "-";
  return numeric.toLocaleString(undefined, { maximumFractionDigits: 0 });
};

export const formatDecimal = (value, decimals = 1) => {
  if (isNil(value)) return "-";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "-";
  return numeric.toFixed(decimals);
};

export const formatPercentage = (value, decimals = 1) => {
  const formatted = formatDecimal(value, decimals);
  return formatted === "-" ? "-" : `${formatted}%`;
};

export const calculateDribbleAttempts = (data) => {
  if (!data) return null;
  const successes =
    typeof data.successful_dribbles === "number"
      ? data.successful_dribbles
      : null;
  const successRate =
    typeof data.dribble_success_percentage === "number"
      ? data.dribble_success_percentage
      : null;

  if (successes === null) {
    return null;
  }

  if (!successRate || successRate <= 0) {
    return successes;
  }

  const attempts = successes / (successRate / 100);
  return Number.isFinite(attempts) ? attempts : successes;
};

export const getTotalDuels = (data) => {
  if (!data) return null;
  if (typeof data.total_contests === "number") {
    return data.total_contests;
  }
  const won =
    typeof data.total_duels_won === "number" ? data.total_duels_won : null;
  const lost = typeof data.duels_lost === "number" ? data.duels_lost : null;

  if (won === null || lost === null) {
    return null;
  }

  return won + lost;
};

export const ensurePassAccuracy = (data) => {
  if (!data) return null;
  if (typeof data.pass_accuracy_percentage === "number") {
    return data.pass_accuracy_percentage;
  }

  const totalPasses =
    typeof data.total_passes_attempted === "number"
      ? data.total_passes_attempted
      : null;
  const successfulPasses =
    typeof data.successful_passes === "number" ? data.successful_passes : null;

  if (!totalPasses || totalPasses <= 0 || successfulPasses === null) {
    return null;
  }

  return (successfulPasses / totalPasses) * 100;
};

export const mergeHomeAwayStats = (homeData, awayData) => {
  const sources = [homeData, awayData].filter(Boolean);
  if (!sources.length) {
    return null;
  }

  const aggregated = {};
  const fieldsToSum = [
    "goals_scored",
    "assists",
    "total_shots_attempted",
    "shots_on_target",
    "shots_off_target",
    "big_chances_missed",
    "big_chances_created",
    "tackles_attempted",
    "interceptions",
    "clearances",
    "fouls_committed",
    "yellow_cards_received",
    "goals_conceded",
    "clean_sheets",
    "total_passes_attempted",
    "successful_passes",
    "key_passes",
    "total_long_passes_attempted",
    "total_crosses",
    "successful_dribbles",
    "dispossessed",
    "fouls_suffered",
    "total_contests",
    "total_duels_won",
    "duels_lost",
    "touches",
    "matches_started",
    "total_appearances",
    "minutes_played",
    "offsides",
  ];

  fieldsToSum.forEach((field) => {
    aggregated[field] = sources.reduce(
      (sum, item) =>
        sum + (typeof item?.[field] === "number" ? item[field] : 0),
      0
    );
  });

  const dribbleAttemptInfo = sources.reduce(
    (acc, item) => {
      const attempts = calculateDribbleAttempts(item);
      if (attempts !== null && attempts !== undefined) {
        return {
          total: acc.total + attempts,
          hasData: true,
        };
      }
      return acc;
    },
    { total: 0, hasData: false }
  );

  aggregated.dribble_attempts = dribbleAttemptInfo.hasData
    ? dribbleAttemptInfo.total
    : null;

  if (dribbleAttemptInfo.hasData) {
    aggregated.dribble_success_percentage =
      dribbleAttemptInfo.total > 0
        ? (aggregated.successful_dribbles / dribbleAttemptInfo.total) * 100
        : 0;
  } else {
    aggregated.dribble_success_percentage = sources.find(
      (item) => typeof item?.dribble_success_percentage === "number"
    )?.dribble_success_percentage;
  }

  const totalPasses = aggregated.total_passes_attempted;
  const successfulPasses = aggregated.successful_passes;

  aggregated.pass_accuracy_percentage =
    totalPasses > 0
      ? (successfulPasses / totalPasses) * 100
      : sources.find(
          (item) => typeof item?.pass_accuracy_percentage === "number"
        )?.pass_accuracy_percentage ?? null;

  aggregated.type = "overall";
  aggregated.season_id =
    homeData?.season_id ?? awayData?.season_id ?? aggregated.season_id ?? null;
  aggregated.season_name =
    homeData?.season_name ??
    awayData?.season_name ??
    aggregated.season_name ??
    null;
  aggregated.player_id =
    homeData?.player_id ?? awayData?.player_id ?? aggregated.player_id ?? null;

  return aggregated;
};

export const aggregateHeatmapPoints = (points = []) => {
  if (!Array.isArray(points) || points.length === 0) {
    return [];
  }

  const accumulator = points.reduce((acc, point) => {
    const x = Number(point?.x);
    const y = Number(point?.y);

    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      return acc;
    }

    const key = `${x}-${y}`;
    if (!acc[key]) {
      acc[key] = { x, y, count: 0 };
    }
    acc[key].count += 1;
    return acc;
  }, {});

  return Object.values(accumulator);
};

export const formatMatchDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
};

export const normalizeMatchId = (value) =>
  value === 0 || value ? String(value) : null;

export const mapStatsFromApi = (data) => {
  const hasData = data && Object.keys(data).length > 0;

  const totalAppearances =
    typeof data?.total_appearances === "number" ? data.total_appearances : null;
  const matchesStarted =
    typeof data?.matches_started === "number" ? data.matches_started : null;
  const minutesPlayed =
    typeof data?.minutes_played === "number" ? data.minutes_played : null;

  const substitutions =
    totalAppearances !== null && matchesStarted !== null
      ? Math.max(totalAppearances - matchesStarted, 0)
      : null;

  const passAccuracy = ensurePassAccuracy(data);
  const dribbleAttempts =
    data?.dribble_attempts ?? calculateDribbleAttempts(data);
  const dribbleSuccess =
    typeof data?.dribble_success_percentage === "number"
      ? data.dribble_success_percentage
      : dribbleAttempts && dribbleAttempts > 0
      ? ((data?.successful_dribbles ?? 0) / dribbleAttempts) * 100
      : null;
  const totalDuels = getTotalDuels(data);

  return {
    keyStats: [
      {
        key: "appearance",
        label: "Appearances",
        value: formatInteger(totalAppearances),
      },
      {
        key: "goals",
        label: "Goals",
        value: formatInteger(data?.goals_scored),
      },
      { key: "wins", label: "Wins", value: "-" },
      { key: "losses", label: "Losses", value: "-" },
      {
        key: "minutesPlayed",
        label: "Minutes Played",
        value: formatInteger(minutesPlayed),
      },
    ],
    sections: [
      {
        id: "attacking",
        title: "Attacking",
        stats: [
          { label: "Goals", value: formatInteger(data?.goals_scored) },
          { label: "Assists", value: formatInteger(data?.assists) },
          {
            label: "Shots Total",
            value: formatInteger(data?.total_shots_attempted),
          },
          {
            label: "Shots On Target",
            value: formatInteger(data?.shots_on_target),
          },
          {
            label: "Shots Off Target",
            value: formatInteger(data?.shots_off_target),
          },
          {
            label: "Big Chances Missed",
            value: formatInteger(data?.big_chances_missed),
          },
          {
            label: "Big Chances Created",
            value: formatInteger(data?.big_chances_created),
          },
        ],
      },
      {
        id: "defending",
        title: "Defending",
        stats: [
          { label: "Tackles", value: formatInteger(data?.tackles_attempted) },
          { label: "Interceptions", value: formatInteger(data?.interceptions) },
          { label: "Clearances", value: formatInteger(data?.clearances) },
          { label: "Fouls", value: formatInteger(data?.fouls_committed) },
          {
            label: "Yellow Cards",
            value: formatInteger(data?.yellow_cards_received),
          },
          {
            label: "Goals Conceded",
            value: formatInteger(data?.goals_conceded),
          },
          { label: "Clean Sheets", value: formatInteger(data?.clean_sheets) },
        ],
      },
      {
        id: "passing",
        title: "Passing",
        stats: [
          {
            label: "Passes",
            value: formatInteger(data?.total_passes_attempted),
          },
          {
            label: "Accurate Passes",
            value: formatInteger(data?.successful_passes),
          },
          { label: "Pass Accuracy", value: formatPercentage(passAccuracy) },
          { label: "Key Passes", value: formatInteger(data?.key_passes) },
          {
            label: "Long Balls",
            value: formatInteger(data?.total_long_passes_attempted),
          },
          { label: "Crosses", value: formatInteger(data?.total_crosses) },
          { label: "Assists", value: formatInteger(data?.assists) },
        ],
      },
      {
        id: "dribbling",
        title: "Dribbling",
        stats: [
          {
            label: "Dribble Attempts",
            value: formatDecimal(dribbleAttempts, 1),
          },
          {
            label: "Successful Dribbles",
            value: formatInteger(data?.successful_dribbles),
          },
          {
            label: "Dribble Success Rate",
            value: formatPercentage(dribbleSuccess),
          },
          { label: "Dispossessed", value: formatInteger(data?.dispossessed) },
          { label: "Fouls Drawn", value: formatInteger(data?.fouls_suffered) },
          { label: "Total Duels", value: formatInteger(totalDuels) },
          { label: "Duels Won", value: formatInteger(data?.total_duels_won) },
        ],
      },
      buildStrengthWeaknessSection(),
      {
        id: "other",
        title: "Other",
        stats: [
          { label: "Minutes Played", value: formatInteger(minutesPlayed) },
          { label: "Appearances", value: formatInteger(totalAppearances) },
          { label: "Matches Started", value: formatInteger(matchesStarted) },
          { label: "Substitutions", value: formatInteger(substitutions) },
          { label: "Touches", value: formatInteger(data?.touches) },
          { label: "Average Rating", value: "-" },
          { label: "Offsides", value: formatInteger(data?.offsides) },
        ],
      },
    ],
    meta: { hasData, source: "api" },
  };
};

const toCandidateArray = (value) => {
  if (value === undefined || value === null) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
};

const getValueByPath = (source, path) => {
  if (!source || typeof source !== "object") {
    return undefined;
  }

  if (!path || typeof path !== "string") {
    return undefined;
  }

  return path.split(".").reduce((accumulator, segment) => {
    if (accumulator === null || accumulator === undefined) {
      return undefined;
    }

    if (typeof accumulator !== "object") {
      return undefined;
    }

    return accumulator[segment];
  }, source);
};

export const resolveStatNumber = (source, candidates) => {
  const paths = toCandidateArray(candidates);
  for (const path of paths) {
    if (typeof path === "function") {
      const computed = path(source);
      const numeric = Number(computed);
      if (Number.isFinite(numeric)) {
        return numeric;
      }
      continue;
    }

    const raw = getValueByPath(source, path);
    if (raw === null || raw === undefined) {
      continue;
    }

    const numeric = Number(raw);
    if (Number.isFinite(numeric)) {
      return numeric;
    }
  }

  return null;
};

export const formatStatCount = (source, candidates) => {
  const numeric = resolveStatNumber(source, candidates);
  if (numeric === null) {
    return "-";
  }

  return formatInteger(numeric);
};

export const formatStatDecimal = (source, candidates, digits = 1) => {
  const numeric = resolveStatNumber(source, candidates);
  if (numeric === null) {
    return "-";
  }

  return formatDecimal(numeric, digits);
};

export const formatStatPercentage = (source, candidates, digits = 1) => {
  const numeric = resolveStatNumber(source, candidates);
  if (numeric === null) {
    return "-";
  }

  const formatted = formatDecimal(numeric, digits);
  return formatted === "-" ? "-" : `${formatted}%`;
};

const computePercentage = (made, total) => {
  if (made === null || total === null) {
    return null;
  }

  if (total === 0) {
    return 0;
  }

  const value = (made / total) * 100;
  return Number.isFinite(value) ? value : null;
};

export const formatStatRatio = (
  source,
  {
    madeKeys,
    totalKeys,
    percentageKeys,
    digits = 1,
  }
) => {
  const made = resolveStatNumber(source, madeKeys);
  const total = resolveStatNumber(source, totalKeys);

  let percentage = resolveStatNumber(source, percentageKeys);
  if (percentage === null) {
    percentage = computePercentage(made, total);
  }

  const formattedPercentage =
    percentage === null ? null : formatDecimal(percentage, digits);
  const percentageLabel =
    formattedPercentage && formattedPercentage !== "-"
      ? `${formattedPercentage}%`
      : null;

  if (made !== null && total !== null && total !== 0) {
    const base = `${formatInteger(made)}/${formatInteger(total)}`;
    if (percentageLabel) {
      return `${base} (${percentageLabel})`;
    }
    return base;
  }

  if (made !== null) {
    if (percentageLabel) {
      return `${formatInteger(made)} (${percentageLabel})`;
    }
    return formatInteger(made);
  }

  if (total !== null) {
    if (percentageLabel) {
      return `${formatInteger(total)} (${percentageLabel})`;
    }
    return formatInteger(total);
  }

  if (percentageLabel) {
    return percentageLabel;
  }

  return "-";
};

export const formatRatingValue = (source, candidates) => {
  const numeric = resolveStatNumber(source, candidates);
  if (numeric === null) {
    return "-";
  }

  const adjusted = numeric > 10 ? numeric / 100 : numeric;
  return formatDecimal(adjusted, 2);
};

/**
 * Derive progress bar color based on percentage
 */
export const deriveProgressColor = (percentage) => {
  if (!Number.isFinite(percentage)) {
    return "#E5E6E8";
  }
  if (percentage < 40) {
    return "#E74341";
  }
  if (percentage < 70) {
    return "#F09D0F";
  }
  return "#4282FC";
};

/**
 * Calculate save percentage for goalkeepers
 */
export const calculateSavePercentage = (saves, goalsAgainst) => {
  const totalShots = saves + goalsAgainst;
  if (totalShots === 0) return 0;
  return Math.round((saves / totalShots) * 100);
};

/**
 * Get stat value from blocks array
 */
export const getStatValueFromBlocks = (blocks, blockName, statKey) => {
  const block = blocks.find((b) => b.name === blockName);
  const stat = block?.stats?.find((s) => s.key === statKey);
  return stat?.value || 0;
};

/**
 * Check if stats data is valid for display
 */
export const isValidStatsData = (seasonStats) => {
  return (
    seasonStats &&
    Array.isArray(seasonStats.blocks) &&
    seasonStats.blocks.length > 0
  );
};